import { FC, useCallback } from 'react'
import { AlgoliaIndex, DesktopSearchFiltersLayout, SearchPagination } from 'search'
import { renderStart } from 'shared/hocs/render'
import styled from 'styled-components'
import SearchSortConnected, { SearchSort } from './SearchSort'

import unionBy from 'lodash/unionBy'
import { useRouter } from 'next/router'
import { pageWidth } from 'shared/lib'
import { AggregationFilters, ProductSearchResponse } from 'shared/types/ConsumerSearch'
import { CSSearchState } from 'shared/utils/search/cs-utils'
import ConnectedFilterHeader from './FilterHeader'
import ResultsCounterConnected, { ResultsCounter } from './ResultsCounter'
import { NewDesktopFilters } from './SearchFilters/DesktopSearchFiltersLayout'
import FilterHeader from './SearchFilters/NewFilters/FilterHeader'
import SearchGrid, { ConsumerSearchGrid } from './SearchGrid'
import { Pagination } from './SearchPagination'

const RightColumn = styled.div`
  display: flex;
  flex: 3;
  flex-direction: column;
  margin-left: 30px;
`
const LeftColumn = styled(RightColumn)`
  margin: 0;
  flex: 1;
`
const Wrapper = styled.div`
  width: 100%;
  display: flex;
  ${pageWidth}
`
const GridHeader = styled.div`
  display: flex;
  justify-content: space-between;
`
type DesktopAlgoliaSearchGridLayoutProps = {
  consumerResults: ProductSearchResponse
  defaultRefinement: AlgoliaIndex
  handleQueryChange?: (attr: string, value: string | number) => void
  hiddenFilters: Set<string>
  initialFilters: AggregationFilters
  initialSearchState: CSSearchState
  nbHits?: number
  sortConfig: {
    value: AlgoliaIndex
    label: string
  }[]
}

const DesktopGridSearchLayout = ({
  consumerResults,
  defaultRefinement,
  hiddenFilters,
  initialFilters,
  initialSearchState,
  sortConfig,
}: DesktopAlgoliaSearchGridLayoutProps) => {
  const router = useRouter()
  const sizeCount = useCallback(() => {
    const mensSizes = router.query?.size_men?.split(',')?.length || 0
    const womensSizes = router.query?.size_women?.split(',')?.length || 0
    const youthSizes = router.query?.size_youth?.split(',')?.length || 0

    return mensSizes + womensSizes + youthSizes
  }, [router.query])

  const handleQueryChange = (attr: string, value: string | number) => {
    let newQuery = { ...(router?.query as Record<string, string>) }

    newQuery = {
      ...newQuery,
      [attr]: `${value}`,
    }

    if (attr !== 'page') {
      delete newQuery.page
    }

    if (newQuery[attr] === '') {
      delete newQuery[attr]
    }

    void router.push(
      `${window.location.pathname}?` + new URLSearchParams(newQuery).toString(),
      undefined,
      {
        shallow: true,
      },
    )
  }

  // get the silhouette list from here
  const newSilhouettesList = (() => {
    // get the current selected models from the initial search state
    const currentSelectedModels = initialSearchState?.refinementList?.silhouette
      ? initialSearchState.refinementList.silhouette.map((s: string) => ({ name: s, count: 1 }))
      : []

    if (
      initialSearchState?.refinementList?.collection_slugs?.[0] ||
      initialSearchState?.refinementList?.brand_name ||
      initialSearchState?.refinementList?.brand
    ) {
      // if there are any filters applied get the available models from the search
      // amd include any current selected model
      return unionBy(
        consumerResults?.aggregation?.filters?.silhouettesList || [],
        currentSelectedModels,
        'name',
      )
    } else {
      // else no filters are applied and we get all models
      // amd include any current selected model
      return unionBy(initialFilters?.silhouettesList || [], currentSelectedModels, 'name')
    }
  })()

  // here make sure the silhouette list is unchanging
  return (
    <Wrapper>
      <LeftColumn>
        <FilterHeader />
        <NewDesktopFilters
          filters={
            (router.query?.page ? initialFilters : consumerResults?.aggregation?.filters) || {}
          }
          silhouettesList={newSilhouettesList}
          hiddenFilters={hiddenFilters}
          isApparel={router.query?.slug?.includes('apparel')}
        />
      </LeftColumn>
      <RightColumn>
        <GridHeader>
          <ResultsCounter nbHits={consumerResults?.total} />
          <SearchSort
            items={sortConfig}
            currentRefinement={defaultRefinement}
            refine={(value) => {
              handleQueryChange?.('sortBy', value)
            }}
          />
        </GridHeader>
        <ConsumerSearchGrid hits={consumerResults?.data || {}} sizeFilterCount={sizeCount()} />
        <Pagination
          currentRefinement={parseInt((router.query?.page as string) || '1')}
          nbPages={
            consumerResults?.data?.length > 0 ? Math.ceil((consumerResults?.total || 0) / 30) : 0
          }
          refine={(value) => {
            handleQueryChange('page', value)
          }}
        />
      </RightColumn>
    </Wrapper>
  )
}

export const ConsumerSearchDesktopGridLayout = renderStart.large(DesktopGridSearchLayout)

const DesktopAlgoliaSearchGridLayout: FC<DesktopAlgoliaSearchGridLayoutProps> = ({
  sortConfig,
  defaultRefinement,
  hiddenFilters,
}) => {
  return (
    <Wrapper>
      <LeftColumn>
        <ConnectedFilterHeader hiddenFilters={hiddenFilters} />
        <DesktopSearchFiltersLayout />
      </LeftColumn>
      <RightColumn>
        <GridHeader>
          <ResultsCounterConnected />
          <SearchSortConnected items={sortConfig} defaultRefinement={defaultRefinement} />
        </GridHeader>
        <SearchGrid />
        <SearchPagination />
      </RightColumn>
    </Wrapper>
  )
}

export default renderStart.large(DesktopAlgoliaSearchGridLayout)
